import { useEffect, useMemo, useState } from "react";
import Modal from "@/components/base/Modal";
import { Field, Input, Select } from "@/components/base/FormControls";
import { formatFcfa } from "@/utils/format";
import { useFormules } from "@/store/formules/FormulesProvider";
import { useSuperAdmin } from "@/pages/super-admin/data/SuperAdminProvider";
import { COMPTE_STATUT_LABELS } from "@/pages/super-admin/data/types";
import { useBilling } from "@/store/billing/BillingProvider";
import {
  ABONNEMENT_STATUT_OPTIONS,
  type PlatformAbonnement,
  type PlatformAbonnementInput,
  type AbonnementStatut,
} from "@/store/billing/types";
import type { Periodicite } from "@/config/formules";

interface AbonnementFormProps {
  open: boolean;
  onClose: () => void;
  abonnement: PlatformAbonnement | null;
}

interface FormState {
  compteId: string;
  formuleId: string;
  periodicite: Periodicite;
  statut: AbonnementStatut;
  dateDebut: string;
  dateFin: string;
  renouvellement: boolean;
}

const EMPTY_STATE: FormState = {
  compteId: "",
  formuleId: "gratuit",
  periodicite: "mensuel",
  statut: "actif",
  dateDebut: "",
  dateFin: "",
  renouvellement: false,
};

const PERIODICITE_OPTIONS = [
  { value: "mensuel", label: "Mensuelle" },
  { value: "annuel", label: "Annuelle" },
];

/**
 * Création / modification d'un abonnement.
 * Le montant est dérivé de la formule choisie (source de vérité `@/config/formules`)
 * et le compte rattaché provient des comptes réellement présents.
 */
export default function AbonnementForm({ open, onClose, abonnement }: AbonnementFormProps) {
  const { request } = useSuperAdminRequest();
  const { formules } = useFormules();
  const { addAbonnement, updateAbonnement } = useBilling();
  const [form, setForm] = useState<FormState>(EMPTY_STATE);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const comptes = request;

  useEffect(() => {
    if (!open) return;
    if (abonnement) {
      setForm({
        compteId: abonnement.compteId,
        formuleId: abonnement.formuleId,
        periodicite: abonnement.periodicite,
        statut: abonnement.statut,
        dateDebut: abonnement.dateDebut,
        dateFin: abonnement.dateFin,
        renouvellement: abonnement.renouvellement,
      });
    } else {
      setForm(EMPTY_STATE);
    }
    setErrors({});
  }, [open, abonnement]);

  const compteOptions = useMemo(
    () => [
      { value: "", label: "Sélectionnez un compte" },
      ...comptes.map((compte) => ({
        value: compte.id,
        label: `${compte.nom} · ${COMPTE_STATUT_LABELS[compte.statut]}`,
      })),
    ],
    [comptes]
  );

  const formuleOptions = formules.map((formule) => ({ value: formule.id, label: formule.nom }));

  const selectedFormule = formules.find((formule) => formule.id === form.formuleId);
  const montant = selectedFormule
    ? form.periodicite === "annuel"
      ? selectedFormule.prixAnnuel
      : selectedFormule.prixMensuel
    : 0;

  const update = (patch: Partial<FormState>) => setForm((prev) => ({ ...prev, ...patch }));

  const handleSubmit = () => {
    const nextErrors: Record<string, string> = {};
    const compte = comptes.find((item) => item.id === form.compteId);
    if (!compte) nextErrors.compteId = "Sélectionnez le compte concerné.";
    if (!form.formuleId) nextErrors.formuleId = "Sélectionnez une formule.";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0 || !compte) return;

    const input: PlatformAbonnementInput = {
      compteId: compte.id,
      compteNom: compte.nom,
      compteType: compte.type,
      formuleId: form.formuleId,
      periodicite: form.periodicite,
      montant,
      statut: form.statut,
      dateDebut: form.dateDebut,
      dateFin: form.dateFin,
      renouvellement: form.renouvellement,
    };

    if (abonnement) updateAbonnement(abonnement.id, input);
    else addAbonnement(input);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={abonnement ? "Modifier l'abonnement" : "Nouvel abonnement"}
      description="Le montant est calculé automatiquement depuis la formule et la périodicité choisies."
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-save-line text-base" aria-hidden="true" />
            Enregistrer
          </button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-x-4 gap-y-3.5 sm:grid-cols-2">
        <Field label="Compte concerné" htmlFor="abo-compte" required error={errors.compteId} className="sm:col-span-2">
          <Select
            id="abo-compte"
            value={form.compteId}
            onChange={(event) => update({ compteId: event.target.value })}
            options={compteOptions}
            placeholder="Sélectionnez un compte"
            invalid={Boolean(errors.compteId)}
          />
        </Field>

        <Field label="Formule" htmlFor="abo-formule" required error={errors.formuleId}>
          <Select
            id="abo-formule"
            value={form.formuleId}
            onChange={(event) => update({ formuleId: event.target.value })}
            options={formuleOptions}
            invalid={Boolean(errors.formuleId)}
          />
        </Field>
        <Field label="Périodicité" htmlFor="abo-periodicite">
          <Select
            id="abo-periodicite"
            value={form.periodicite}
            onChange={(event) => update({ periodicite: event.target.value as Periodicite })}
            options={PERIODICITE_OPTIONS}
          />
        </Field>

        <Field label="Montant (FCFA)" htmlFor="abo-montant" hint="Dérivé de la formule — non modifiable ici.">
          <Input id="abo-montant" value={formatFcfa(montant)} readOnly disabled />
        </Field>
        <Field label="Statut" htmlFor="abo-statut">
          <Select
            id="abo-statut"
            value={form.statut}
            onChange={(event) => update({ statut: event.target.value as AbonnementStatut })}
            options={ABONNEMENT_STATUT_OPTIONS}
          />
        </Field>

        <Field label="Date de début" htmlFor="abo-debut">
          <Input
            id="abo-debut"
            type="date"
            value={form.dateDebut}
            onChange={(event) => update({ dateDebut: event.target.value })}
          />
        </Field>
        <Field label="Date de fin" htmlFor="abo-fin">
          <Input
            id="abo-fin"
            type="date"
            value={form.dateFin}
            onChange={(event) => update({ dateFin: event.target.value })}
          />
        </Field>

        <label
          htmlFor="abo-renouvellement"
          className="flex cursor-pointer items-center gap-2.5 rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5 sm:col-span-2"
        >
          <input
            id="abo-renouvellement"
            type="checkbox"
            checked={form.renouvellement}
            onChange={(event) => update({ renouvellement: event.target.checked })}
            className="h-4 w-4 accent-primary-500"
          />
          <span className="text-sm text-foreground-800">Renouvellement automatique à l'échéance</span>
        </label>
      </div>
    </Modal>
  );
}

/**
 * Petit hook interne : renvoie les comptes du Super Admin pour alimenter la
 * sélection de compte. Isolé pour garder le formulaire focalisé.
 */
function useSuperAdminRequest() {
  const { comptes } = useSuperAdmin();
  return { request: comptes };
}